import { useMemo, useState, useContext } from 'react'

import type { Service, Entitlement, OrganizationAction } from './graphqlEnums'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'
import { isNotNull } from '~publish/helpers/typeGuards'
import { isFreeUser } from '~publish/helpers/user'

import { AccountContext } from './AccountContext'
import type {
  AccountContextValue,
  AccountData,
  CurrentOrganization,
  Billing,
  OBBilling,
  Limits,
  PlanType,
} from './types'

const loggedOutAccountContextValue: AccountContextValue = {
  account: null,
  loading: false,
}

export const useAccount = (): AccountContextValue => {
  const context = useContext(AccountContext)

  return context ?? loggedOutAccountContextValue
}

export const useCurrentOrganization = (): NonNullable<
  AccountData['currentOrganization']
> | null => {
  const { account } = useAccount()

  return account?.currentOrganization ?? null
}

export const useAccountPreferences = (): AccountData['preferences'] | null => {
  const { account } = useAccount()
  return account?.preferences ?? null
}

export const useAccountTimezone = (): string => {
  const { account } = useAccount()
  return account?.timezone ?? CURRENT_TIME_ZONE
}

export const useIsOneBufferOrg = (): boolean => {
  const currentOrganization = useCurrentOrganization()
  return !!currentOrganization?.isOneBufferOrganization
}

// TODO: publish-revamp-refactor: Review this hook as I doubt it does what it seems.
// It's only used once in AppPages, starts as false, and is repeatedly set to true, meaning it's almost always "the first page load".
// Custom hook to keep track if the homepage has been initially loaded
export const usePageLoad = (): {
  initialPageLoad: boolean
  updateInitialPageLoad: () => void
} => {
  const [initialPageLoad, setInitialPageLoad] = useState(false)

  const updateInitialPageLoad = (): void => {
    setInitialPageLoad(true)
  }
  return { initialPageLoad, updateInitialPageLoad }
}

export const useDismissedBanners = (): string[] => {
  const { account } = useAccount()
  const dismissedBanners = account?.dismissedBanners ?? []

  return dismissedBanners?.filter(isNotNull) ?? []
}

export const useChannelsConnected = (): CurrentOrganization['channels'] => {
  const { channels } = useCurrentOrganization() ?? {}
  return channels?.filter(isNotNull) ?? []
}

export const useUnlockedChannels = (): CurrentOrganization['channels'] => {
  const { channels } = useCurrentOrganization() ?? {}

  return channels?.filter((channel) => channel && !channel.isLocked) ?? []
}

export const useHasChannelConnected = (channel: Service): boolean => {
  return (
    useCurrentOrganization()?.channels?.some(
      (currentChannel) => currentChannel.service === channel,
    ) ?? false
  )
}

export const useHasEntitlement = (entitlement: Entitlement): boolean => {
  return useCurrentOrganization()?.entitlements?.includes(entitlement) ?? false
}

export const useOrganizationLimits = (): Limits | undefined => {
  return useCurrentOrganization()?.limits
}

export const useOrganizationBilling = (): Billing | undefined => {
  const { billing } = useCurrentOrganization() ?? {}
  return billing
}

export const useOneBufferBilling = (): OBBilling | null => {
  const { billing } = useCurrentOrganization() ?? {}
  return billing?.__typename === 'OBBilling' ? billing : null
}

export const useOrganizationMembersTotal = (): number | undefined => {
  return useCurrentOrganization()?.members?.totalCount
}

export const useHasPaymentDetails = (): boolean => {
  const { billing } = useCurrentOrganization() ?? {}

  if (billing?.__typename === 'OBBilling') {
    return !!billing.paymentDetails?.hasPaymentDetails
  }

  return false
}

export const useAccountId = (): string | null => {
  const { account } = useAccount()
  return account?.id ?? null
}

export const useAccountEmail = (): string | null => {
  const { account } = useAccount()
  return account?.email ?? null
}

export const useOrganizationId = (): string | null => {
  const currentOrganization = useCurrentOrganization()
  return currentOrganization?.id ?? null
}

export const useSubscription = (): OBBilling['subscription'] | null => {
  const billing = useOneBufferBilling()
  return billing?.subscription ?? null
}

export const useCanStartTrial = (): boolean => {
  const billing = useOneBufferBilling()

  return billing?.canStartTrial ?? false
}

export function usePlan(): PlanType {
  const subscription = useSubscription()
  const currentOrganization = useCurrentOrganization()

  const id = subscription?.plan?.id ?? ''
  const isOneBufferOrganization = Boolean(
    currentOrganization?.isOneBufferOrganization,
  )

  return useMemo(() => {
    return {
      id,
      isOneBufferOrganization,
    }
  }, [id, isOneBufferOrganization])
}

export const useIsLegacyPlan = (): boolean => {
  const { isOneBufferOrganization } = useCurrentOrganization() ?? {}
  return !isOneBufferOrganization
}

export const useOrganizationRole = (): string | null => {
  const currentOrganization = useCurrentOrganization()
  return currentOrganization?.role ?? null
}

/**
 * Whether the actor is allowed to perform `action` on the current
 * organization. The single source of truth for organization-scoped UI gating:
 * the server computes `allowedActions` from the actor's role, lock state and
 * email verification, so callers never re-derive those conditions.
 */
export const useHasOrganizationAction = (
  action: OrganizationAction,
): boolean => {
  const currentOrganization = useCurrentOrganization()
  return currentOrganization?.allowedActions.includes(action) ?? false
}

export const useIsFreeUser = (): boolean => {
  const { account } = useAccount()
  return isFreeUser(account)
}

export const useIsBetaEnabled = (): boolean => {
  const currentOrganization = useCurrentOrganization()
  return currentOrganization?.isBetaEnabled ?? false
}

export const useIsOwner = (): boolean => {
  const { account } = useAccount()
  const currentOrganization = useCurrentOrganization()

  return !!account?.email && account.email === currentOrganization?.ownerEmail
}

export const usePublishUserId = (): string | null => {
  const { account } = useAccount()
  const publishProduct = account?.products?.find(
    (product) => product.name === 'publish',
  )
  return publishProduct?.userId ?? null
}
